import { ApiError, OFFLINE } from "../api/client.js";
import type { AnswerBody, BankSurface, BundleView, Clarification, DraftOutcome, NextAction, RefusedStep } from "../api/types.js";
import { SAY, shownRule } from "./bank-words.js";

/**
 * DCTA-120 — the voice rule (DCTA-118 review §4, UX spec v3 §F.2), as Umakhan ruled it on 5 Oct (DCTA-124,
 * from an iPhone test of the live site; it overrides §F where they differ):
 *
 *  - **The Bank surface** is ONLY the card the customer signs and its result after signing (sent, held,
 *    refused when it ran).
 *  - **Everything else is the assistant's message** — a question, a refusal by a `POL-*` or `FW-*` rule,
 *    KYC, the dry run, "nothing was sent", busy, unavailable, no answer at all (`OFFLINE`), the give-up,
 *    the add-payee pointer. A sentence the bank sent is said verbatim (screen = record); no Bank tag, no
 *    "Not sent" pill; a rule id or a reason code only in judge mode.
 * What is decided here is still WHO said it: the assistant's own limit ("I" is right) or the bank's
 * sentence, said by the assistant.
 */

/** The assistant's limit, not a bank decision: drawn as its line with the bank's sentence verbatim. */
export function assistantSide(r: RefusedStep): boolean {
  return r.reason_code === "SCOPE_UNSUPPORTED";
}

/** The screens this app can open for a `bank_surface` action; anything else draws nothing (§F.3). */
const KNOWN_SURFACES: ReadonlySet<string> = new Set<BankSurface>(["add_payee", "limits", "activity"]);

/** How the app opens each of the bank's own screens. A screen it has no opener for is left out. */
export type SurfaceOpeners = Readonly<Partial<Record<BankSurface, () => void>>>;

/** §F.2 row 5: the assistant's way into one of the bank's own screens. */
export interface Pointer {
  readonly label: string;
  /** The chip's small line: the bank's next step, without a sentence's full stop (a chip is not prose). */
  readonly small: string;
  readonly surface: BankSurface;
}

/**
 * A `bank_surface` action as a chip — add a payee (§F.2 row 5) or, since DCTA-153 ruling 3, the Limits page a cap
 * refusal points to instead of naming a figure — or nothing for a screen this app does not know (never a button that
 * does nothing, §F.3).
 */
export function pointerOf(r: RefusedStep): Pointer | undefined {
  const offered = r.next_actions?.[0];
  if (offered === undefined || offered.action.kind !== "bank_surface" || !KNOWN_SURFACES.has(offered.action.surface)) return undefined;
  const small = (r.next_steps?.[0] ?? "").trim().replace(/\.$/, "");
  return { label: offered.label, small, surface: offered.action.surface };
}

/** The pointer, only when the app can open its screen here (no opener: no chip, §F.3). */
export function wayOn(r: RefusedStep, openers: SurfaceOpeners): Pointer | undefined {
  const pointer = pointerOf(r);
  return pointer === undefined || openers[pointer.surface] === undefined ? undefined : pointer;
}

/**
 * DCTA-154: a `bank_surface` action opens ITS screen through the app's opener for it — "See my limits" the Limits page,
 * "Add a payee" 17 Payees at `add` — and says whether it did. (Before, every bank_surface opened add a payee.)
 */
export function openSurface(action: NextAction, openers: SurfaceOpeners): boolean {
  if (action.kind !== "bank_surface" || !KNOWN_SURFACES.has(action.surface)) return false;
  const open = openers[action.surface];
  if (open === undefined) return false;
  open();
  return true;
}

/**
 * DCTA-154 F27 (b) (backend DCTA-157 B38): the bank refused a payment because the customer answered Yes at the scam check
 * for that payee earlier in the errand (`SCAM_CHECK_PAUSED`, `POL-SCAM-YES-CARRIED`) — drawn as the card's paused end,
 * not as a refusal's message.
 */
export function pausedByBank(r: RefusedStep): boolean {
  return r.reason_code === "SCAM_CHECK_PAUSED";
}

/**
 * R-U1 (§F.2 row 8): a question about a payee no saved payee names — the bank asks, never guesses
 * (AC-08.1). Under it the app draws the add-payee chip (8b); it never invites a number for someone new.
 */
export function unresolvedPayee(q: Clarification): boolean {
  if (!(q.field_path ?? "").endsWith("/payee_ref")) return false;
  // F10 (DCTA-132, §F.2 15i): the near-miss question — "Sara isn't one of your payees. Did you mean Sarah?" — is the
  // bank's ambiguous one answerable in words; a customer who meant someone new gets 8b's way in under it too.
  // Two Johns are not this question: both are saved. DCTA-136: since DCTA-135 every payee question takes words, so words
  // no longer tell them apart — the near miss offers the ONE saved payee closest to the name (ask xx: a signal of its own).
  // §F.2 6h (one registered name, words too) is not it either: its way out is "Don't send".
  if (nameDivergence(q)) return false;
  return q.reason_code === "REFERENCE_UNRESOLVED" || nearMiss(q);
}

/**
 * §F.2 15i, the near miss — "Sara isn't one of your payees. Did you mean Sarah?": a payee question the bank asks with
 * the ONE saved payee closest to the name, words taken. Its "no" is the bank's to read (B20), never ended here (F15).
 */
export function nearMiss(q: Clarification): boolean {
  if (!(q.field_path ?? "").endsWith("/payee_ref") || nameDivergence(q)) return false;
  return q.reason_code === "REFERENCE_AMBIGUOUS" && q.accepts_words === true && (q.candidates?.length ?? q.choices?.length ?? 0) === 1;
}

/**
 * §F.2 6h: the customer said a number and a name that do not go together (`POL-COP-NAME-DIVERGENCE`, a
 * question). Beside the bank's one answer (the registered name) the app offers "Don't send", which ends it.
 */
export function nameDivergence(q: Clarification): boolean {
  return (q.rule_ids ?? []).includes("POL-COP-NAME-DIVERGENCE");
}

/**
 * A request that ended without an outcome (§F.2 row 4), and who is speaking — both are said by the
 * assistant (DCTA-124):
 *  - `line`: the assistant's own — busy, unavailable, no answer, a bank that failed; the words go back to
 *    the field (nothing was decided, so trying again is one tap);
 *  - `bank`: the bank decided (a dry-run refusal, a request it would not take) — its sentence verbatim,
 *    and for judge mode its first rule id (never a screening or structuring one, `shownRule`).
 * `code` is the reason code, for judge mode only.
 */
export type Failure =
  | { readonly kind: "line"; readonly text: string; readonly code: string }
  | { readonly kind: "bank"; readonly lead: string; readonly rule?: string; readonly code: string };

export function failureOf(error: unknown): Failure {
  if (!(error instanceof ApiError)) return { kind: "line", text: SAY.offline, code: "INTERNAL_ERROR" };
  const code = error.reasonCode;
  const said = error.serverMessage;
  if (code === "SERVICE_UNAVAILABLE") return { kind: "line", text: said ?? SAY.unavailable, code };
  if (code === "RATE_LIMITED") return { kind: "line", text: SAY.busy, code };
  // DCTA-126 (§F.2 13i): the PayNow name check down is a 503 that still says the bank's own sentence — the bank's.
  const nameCheckDown = code === "SCREENING_UNAVAILABLE" && said !== undefined;
  if (!nameCheckDown && (code === OFFLINE || error.status >= 500)) return { kind: "line", text: SAY.offline, code };
  const rules = error.body["rule_ids"];
  const rule = Array.isArray(rules) ? shownRule(rules.filter((r): r is string => typeof r === "string")) : undefined;
  return { kind: "bank", lead: said ?? "Nothing was sent.", ...(rule === undefined ? {} : { rule }), code };
}

/** §F.2 11s: the words for an answer to a question the bank no longer holds, when it sent none. */
const QUESTION_GONE = "That question isn't open any more. Say it again to start over.";

/**
 * A question that ended at the bank (DCTA-124, §F.2 11s, F4): an answer to a question that timed out,
 * an answer it does not take, or — on the answer route only — a question it no longer holds
 * (`TXN_NOT_FOUND`). Each is the assistant's line, never a Bank card: the bank's sentence verbatim
 * (11s's own words when it sent none). `undefined`: not a question's end. At signing,
 * `TXN_NOT_FOUND` is the gateway's refusal (11r) and is not this.
 */
export function questionGone(error: unknown, answering: boolean): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const code = error.reasonCode;
  if (code === "CLARIFICATION_EXPIRED" || code === "CLARIFICATION_ANSWER_INVALID") return error.serverMessage;
  if (answering && code === "TXN_NOT_FOUND") return error.serverMessage ?? QUESTION_GONE;
  return undefined;
}

/**
 * DCTA-136 (phrase map F19): the bank did not take an answer and still holds its question — the answer route refuses
 * BEFORE it claims the question (`CLARIFICATION_ANSWER_INVALID`, lab/dcta drafts.ts). The app says the bank's sentence
 * (`lead`, verbatim; none when it sent none) and asks the same question again with its chips; it never lets the
 * question go, so the customer's next words still answer it. `undefined`: not this — a question that ended
 * (`questionGone`) or a bank that did not answer.
 */
export function answerRefused(error: unknown): { readonly lead: string | undefined } | undefined {
  if (!(error instanceof ApiError) || error.reasonCode !== "CLARIFICATION_ANSWER_INVALID") return undefined;
  return { lead: error.serverMessage };
}

/**
 * DCTA-136 (F19, R5): words typed as an answer that the firewall refused whole — "refused BEFORE the question is
 * claimed — it stays open for an honest answer" (lab/dcta drafts.ts). The bank's refusal is said as any refusal is;
 * the question is then asked again. Only that refusal: an answer that drew a card, another question or a policy
 * refusal is the answer's own result.
 */
export function answerBlocked(outcome: DraftOutcome): boolean {
  const refused = outcome.refused_steps ?? [];
  return (
    outcome.kind === "refusal" &&
    outcome.bundle_id === undefined &&
    (outcome.clarifications?.length ?? 0) === 0 &&
    refused.length > 0 &&
    refused.every((r) => r.reason_code === "INSTRUCTION_OVERRIDE_BLOCKED")
  );
}

/**
 * DCTA-151 (F22, §F.2 20i · 20j): after the firewall refused an answer (`answerBlocked`), the question is asked again
 * only when the answer carried the customer's own words (`words` in the answer body): the firewall read them, and the
 * bank kept the question for an honest answer (20i). A chip or a typed figure carries nothing the firewall reads, so
 * its refusal is of the question's own message (`answer_source`), which no answer can complete (20j): row 1's message
 * stands with "Talk to us", the question is spent, and nothing is asked again.
 */
export function reasksAfterBlock(outcome: DraftOutcome, answered: AnswerBody): boolean {
  return answerBlocked(outcome) && "words" in answered;
}

/**
 * DCTA-136 (F10): the bank's flags on a payload's display strings ride beside the payload on the answer that made the
 * bundle (`firewall.data_flags`, DCTA-102); `GET /v1/bundles/{id}` did not carry them when this was built. The card is
 * drawn from the bundle read, with the flags of THAT bundle's own answer when its read has none — two bank answers
 * about one bundle id, never chat state, never model text. A read that carries its own flags wins.
 */
export function withFlags(bundle: BundleView, outcome: DraftOutcome): BundleView {
  if (bundle.firewall !== undefined || outcome.firewall === undefined || outcome.bundle_id !== bundle.bundle_id) return bundle;
  return { ...bundle, firewall: outcome.firewall };
}

/**
 * DCTA-160b: what the bank left out of a card (`skipped_steps`: a part the customer skipped, DCTA-156 R1; a funding move
 * PQ-16 found unneeded, DCTA-160) rides on the answer that made the bundle — the bundle read does not carry it. Carried
 * beside THAT bundle, as the flags are (`withFlags`): two bank answers about one bundle id, never chat state. A read
 * that carries its own wins.
 */
export function withSkipped(bundle: BundleView, outcome: DraftOutcome): BundleView {
  const skipped = outcome.skipped_steps;
  if (bundle.skipped_steps !== undefined || skipped === undefined || skipped.length === 0 || outcome.bundle_id !== bundle.bundle_id) return bundle;
  return { ...bundle, skipped_steps: skipped };
}

/**
 * DCTA-221 (docs/specs/fe-answer-swallowed.md change 2; 09-scheduled.md row 27p): the bank's sentence for a payment set for
 * its day without the time the customer said rides on the answer that made the bundle (`schedule_note`) — the bundle read
 * does not carry it. Carried beside THAT bundle, as `withSkipped` carries what was left out; a read that carries its own wins.
 */
export function withNote(bundle: BundleView, outcome: DraftOutcome): BundleView {
  const note = outcome.schedule_note;
  if (bundle.schedule_note !== undefined || note === undefined || note === "" || outcome.bundle_id !== bundle.bundle_id) return bundle;
  return { ...bundle, schedule_note: note };
}

/**
 * DCTA-127: the assistant's reply to words that asked for no errand — a greeting, thanks, "what can
 * you do?", nothing heard. The bank picks one fixed sentence (never model text) and the chat prints it
 * exactly as sent, as the assistant's line: nothing to sign, nothing sent, nothing refused, so no Bank
 * tag and no "Nothing was sent". Only an `assistant_reply` carries one; one with no words is none.
 */
/**
 * DCTA-226 (§5.7): the bank's answer to words asked at an offer left the offer open — its chips stay live. The bank's word
 * (`question_kept_open`) decides when it is sent. Today's bank writes it in the audit record only, and its answer to words
 * at an offer is always one of its readers' replies, which never claim the offer (lab/dcta drafts.ts, D226-S7): a reply
 * with no such field keeps the offer too. Anything else (a refusal, a card) ends it.
 */
export function offerKept(outcome: DraftOutcome): boolean {
  const said: unknown = outcome.question_kept_open;
  if (said !== undefined) return said === "true";
  return outcome.kind === "assistant_reply" && outcome.bundle_id === undefined;
}

export function replyOf(outcome: DraftOutcome): string | undefined {
  if (outcome.kind !== "assistant_reply") return undefined;
  const text = outcome.reply?.text;
  return text === undefined || text.trim() === "" ? undefined : text;
}

/**
 * DCTA-198 (code review DCTA-193 P3-B4): the bank's sentence for a correction that kept a signed card ("The earlier card is
 * already signed, so it wasn't replaced. If you don't want it, cancel it from that card." / "The earlier payment already
 * went, so this card is a new payment."), drawn verbatim above the new card — or nothing, when the answer has no such line —
 * and the kept card's id for Claude's ruling 3 ("Show the earlier card"; none when the bank named no card).
 */
export function keptLine(outcome: DraftOutcome): { readonly line: string; readonly bundleId?: string } | undefined {
  const message: unknown = outcome.open_card_kept?.message;
  const bundleId: unknown = outcome.open_card_kept?.bundle_id;
  if (typeof message !== "string" || message.trim() === "") return undefined;
  return typeof bundleId === "string" && bundleId !== "" ? { line: message, bundleId } : { line: message };
}
